/**
 * 全量场景列表组件(FullSceneList)。
 *
 * 从 SceneGeneration 页的「全量场景列表」Tab 抽取而来,让 SceneManagement 默认
 * 视图和 SceneGeneration 都能复用同一份列表 UI + 行为(筛选 + 批量禁用/构建 +
 * 单条禁用/恢复/保密切换 + 场景名点进配置文件页 + 评论 Drawer 可选)。
 *
 * 与两个入口的差异抽象成 props:
 * - ``fromKey``:场景名点击跳 /scene-bos-files/:id?from=<fromKey> 时的来源标记,
 *   用于配置文件页返回时的定位。SceneManagement 传 'management',SceneGeneration 传 'generation'。
 * - ``showCommentColumn``:是否显示「评论」列。SceneManagement 已下线该列,传 false;
 *   SceneGeneration 保留原行为,传 true(默认)。
 * - ``syncUrl``:是否把筛选 / 分页写入 URL query。SceneGeneration 需要(单页专有),
 *   SceneManagement 不写(默认视图 URL 不需要额外污染)。
 *
 * 组件内部持有:filters state + fetch + columns + rowSelection + batch actions + comment drawer。
 * 外部无需感知任何数据链路;传入 sceneTree(供 filter 里的 TreeSelect 用)即可。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Button, DatePicker, Modal, Popconfirm, Popover, Select, Space, Table, Tag, Tooltip, TreeSelect, Typography, App,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  CommentOutlined, FileOutlined, FolderAddOutlined, FolderOutlined, LockFilled, MoreOutlined, PlayCircleOutlined,
  RollbackOutlined, SearchOutlined, StopOutlined, UnlockOutlined,
} from '@ant-design/icons';

import SceneCommentDrawer, { useSceneCommentSummary } from '@components/SceneCommentDrawer';
import SceneSourceTag, { SCENE_SOURCE_FILTER_OPTIONS } from '@components/SceneSourceTag';
import ApiCodeLink from '@components/ApiCodeLink';
import { SceneStatusTag } from '@components/StatusTag';
import { ResizableTitle, useResizableColumns } from '@components/ResizableTable';
import { useCurrentUser } from '@/common/auth/adminAcl';
import { useEnterToSearch } from '@/common/keyboard';
import { formatBatchAddResult } from '@/utils/sceneSetAddResult';
import {
  batchAddScenesToTree, batchGenerateSceneByIds, disableScenes, getSceneInspectionList, getSceneLabels,
  getSceneStatusList,
  restoreScenes, setScenesSecurityLevel,
  type SceneGenMode, type SceneInspectionItem, type SceneSource, type SceneTreeNode,
  type StatusOption,
} from '@/apis/simulation';

const { Text } = Typography;

const TOKEN_SEPARATORS = [',', ' ', '，', ';', '；'];
const TIME_PICKER_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const formatTime = (val: string | null | undefined) =>
  val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-';

const formatLabelDisplay = (v: unknown): string => {
  if (v == null) return '-';
  if (typeof v !== 'string') return String(v);
  const t = v.trim();
  if (t === '') return '-';
  const low = t.toLowerCase();
  if (low === 'null' || low === 'undefined') return '-';
  return v;
};

// —— URL 持久化辅助(仅 syncUrl=true 时使用) ——
const toCsv = (tags: string[] | undefined): string | undefined => {
  if (!tags?.length) return undefined;
  const cleaned = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
  return cleaned.length ? cleaned.join(',') : undefined;
};
const fromCsv = (raw: string | null): string[] => {
  if (!raw) return [];
  return Array.from(new Set(raw.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)));
};
const toIso = (d: Dayjs | null | undefined): string | undefined => {
  if (!d || !d.isValid()) return undefined;
  return d.format('YYYY-MM-DDTHH:mm:ss');
};
const fromIso = (raw: string | null): Dayjs | null => {
  if (!raw) return null;
  const d = dayjs(raw);
  return d.isValid() ? d : null;
};

// —— 请求 body 用的清洗(去空 + 去重) ——
const cleanTags = (tags: string[] | undefined): string[] | undefined => {
  if (!tags?.length) return undefined;
  const cleaned = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
  return cleaned.length ? cleaned : undefined;
};
const cleanIdTags = (tags: string[] | undefined): number[] | undefined => {
  const cleaned = cleanTags(tags);
  if (!cleaned) return undefined;
  const nums = cleaned
    .map(t => Number(t))
    .filter(n => Number.isFinite(n) && Number.isInteger(n));
  return nums.length ? nums : undefined;
};

interface Filters {
  ids: string[];
  names: string[];
  status: number | undefined;
  jiras: string[];
  labels: string[];
  sources: string[];
  regions: string[];
  genModes: string[];
  treeNodeId: number | undefined;
  createTimeRange: [Dayjs, Dayjs] | null;
}

const emptyFilters: Filters = {
  ids: [], names: [], status: undefined, jiras: [],
  labels: [], sources: [], regions: [], genModes: [], treeNodeId: undefined, createTimeRange: null,
};

/** 场景树 → TreeSelect data(锁定节点保留但禁选)。 */
function buildFilterTreeData(tree: SceneTreeNode[]): Record<string, unknown>[] {
  const build = (nodes: SceneTreeNode[]): Record<string, unknown>[] =>
    nodes
      .filter(n => !n.isHidden)
      .map(n => ({
        title: n.name,
        value: n.id,
        key: n.id,
        selectable: !n.isLocked,
        disabled: !!n.isLocked,
        children: n.children?.length ? build(n.children) : undefined,
      }));
  return build(tree);
}

/** 场景树 → 仅叶子(场景集,nodeFlag===1)可选的 TreeSelect data。
 *  用于「加入场景集」Modal:目录不能作为目标(接口按 tree_node_id 精确挂,
 *  只有叶子才是承载场景的集合)。节点带图标,与左侧场景树视觉对齐:
 *    目录 = FolderOutlined 琥珀色 / 叶子 = FileOutlined 灰色 */
function buildLeafOnlyTreeData(tree: SceneTreeNode[]): Record<string, unknown>[] {
  const build = (nodes: SceneTreeNode[]): Record<string, unknown>[] =>
    nodes
      .filter(n => !n.isHidden)
      .map(n => {
        const isLeaf = n.nodeFlag === 1;
        return {
          // title 用 ReactNode 挂图标;search 依赖 string,故另开 name 字段配 treeNodeFilterProp="name"
          title: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {isLeaf
                ? <FileOutlined style={{ color: '#999' }} />
                : <FolderOutlined style={{ color: '#faad14' }} />}
              {n.name}
            </span>
          ),
          name: n.name,
          value: n.id,
          key: n.id,
          selectable: isLeaf && !n.isLocked,
          disabled: !isLeaf || !!n.isLocked,
          children: n.children?.length ? build(n.children) : undefined,
        };
      });
  return build(tree);
}

export interface FullSceneListProps {
  /** 场景树数据(供 TreeSelect 筛选;由父组件持有,避免每处重复拉) */
  sceneTree: SceneTreeNode[];
  /** 场景名点击跳配置文件页时的 ``?from=`` 标记 */
  fromKey?: 'management' | 'generation';
  /** 是否显示「评论」列;默认 true(SceneGeneration 保留),SceneManagement 传 false */
  showCommentColumn?: boolean;
  /** 是否把筛选/分页写入 URL;默认 false。SceneGeneration 传 true(单页 URL 持久化) */
  syncUrl?: boolean;
}

const FullSceneList: React.FC<FullSceneListProps> = ({
  sceneTree,
  fromKey = 'management',
  showCommentColumn = true,
  syncUrl = false,
}) => {
  const { message } = App.useApp();
  const { hasPerm } = useCurrentUser();
  const canManageSecurity = hasPerm('data:confidential:manage');
  const [searchParams, setSearchParams] = useSearchParams();

  // —— filters (可 URL 初始化) ——
  const initFromUrl = useCallback((): Filters & { page: number; pageSize: number } => {
    if (!syncUrl) return { ...emptyFilters, page: 1, pageSize: 15 };
    const sp = searchParams;
    const rawStatus = sp.get('status');
    const status = rawStatus != null && rawStatus !== '' ? Number(rawStatus) : undefined;
    const treeNodeIdRaw = sp.get('treeNodeId');
    const treeNodeIdNum = treeNodeIdRaw ? Number(treeNodeIdRaw) : NaN;
    const cts = fromIso(sp.get('createTimeStart'));
    const cte = fromIso(sp.get('createTimeEnd'));
    return {
      ids: fromCsv(sp.get('ids')),
      names: fromCsv(sp.get('names')),
      status: Number.isFinite(status) ? status : undefined,
      jiras: fromCsv(sp.get('jiraIssueCodes')),
      labels: fromCsv(sp.get('labels')),
      sources: fromCsv(sp.get('sources')),
      regions: fromCsv(sp.get('regions')),
      genModes: fromCsv(sp.get('genModes')),
      treeNodeId: Number.isFinite(treeNodeIdNum) && treeNodeIdNum > 0 ? treeNodeIdNum : undefined,
      createTimeRange: cts && cte ? [cts, cte] : null,
      page: Math.max(1, Number(sp.get('page')) || 1),
      pageSize: Math.max(1, Number(sp.get('pageSize')) || 15),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncUrl]);

  const initial = useMemo(initFromUrl, [initFromUrl]);
  const [filterIds, setFilterIds] = useState<string[]>(initial.ids);
  const [filterNames, setFilterNames] = useState<string[]>(initial.names);
  const [filterStatus, setFilterStatus] = useState<number | undefined>(initial.status);
  const [filterJiras, setFilterJiras] = useState<string[]>(initial.jiras);
  const [filterLabels, setFilterLabels] = useState<string[]>(initial.labels);
  const [filterSources, setFilterSources] = useState<string[]>(initial.sources);
  const [filterRegions, setFilterRegions] = useState<string[]>(initial.regions);
  const [filterGenModes, setFilterGenModes] = useState<string[]>(initial.genModes);
  const [filterTreeNodeId, setFilterTreeNodeId] = useState<number | undefined>(initial.treeNodeId);
  const [filterCreateTimeRange, setFilterCreateTimeRange] = useState<[Dayjs, Dayjs] | null>(initial.createTimeRange);


  const [scenes, setScenes] = useState<SceneInspectionItem[]>([]);
  const [sceneLoading, setSceneLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: initial.page,
    pageSize: initial.pageSize,
    total: 0,
  });

  const [labelOptions, setLabelOptions] = useState<string[]>([]);
  const [labelLoading, setLabelLoading] = useState(false);
  // 场景状态下拉:接口驱动(SceneStatusEnum 后端单点),避免硬编码漏改
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);

  // 表格滚动容器 ref:传给 Table sticky.getContainer,让表头在容器内滚时钉在顶部。
  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  const [selectedSceneIds, setSelectedSceneIds] = useState<React.Key[]>([]);
  const [batchBuilding, setBatchBuilding] = useState(false);
  const [batchDisabling, setBatchDisabling] = useState(false);
  // 「加入集合」Modal 状态:target = 目标叶子节点 id;submitting = loading
  const [addToSetOpen, setAddToSetOpen] = useState(false);
  const [addToSetTarget, setAddToSetTarget] = useState<number | undefined>(undefined);
  const [addToSetSubmitting, setAddToSetSubmitting] = useState(false);
  const [togglingSceneId, setTogglingSceneId] = useState<number | null>(null);
  const [actionPopoverSceneId, setActionPopoverSceneId] = useState<number | null>(null);

  // 评论 Drawer(仅 showCommentColumn=true 时启用)
  const [commentTarget, setCommentTarget] = useState<{ sceneId: number; sceneName: string | null } | null>(null);
  const sceneIdsForComment = useMemo(
    () => Array.from(new Set(scenes.map(s => s.id).filter(x => x > 0))),
    [scenes],
  );
  const { summary: commentSummary, refresh: refreshCommentSummary } =
    useSceneCommentSummary(showCommentColumn ? sceneIdsForComment : []);

  const filterTreeData = useMemo(() => buildFilterTreeData(sceneTree), [sceneTree]);
  // 「加入集合」Modal 的树:只有叶子(场景集)可选
  const leafOnlyTreeData = useMemo(() => buildLeafOnlyTreeData(sceneTree), [sceneTree]);

  const fetchScenes = useCallback(async (
    current = 1,
    pageSize = 15,
    overrides?: Partial<Filters>,
  ) => {
    const ids = overrides?.ids ?? filterIds;
    const names = overrides?.names ?? filterNames;
    const status = overrides && 'status' in overrides ? overrides.status : filterStatus;
    const jiras = overrides?.jiras ?? filterJiras;
    const labels = overrides?.labels ?? filterLabels;
    const sources = overrides?.sources ?? filterSources;
    const regions = overrides?.regions ?? filterRegions;
    const genModes = overrides?.genModes ?? filterGenModes;
    const treeNodeId = overrides && 'treeNodeId' in overrides ? overrides.treeNodeId : filterTreeNodeId;
    const createTimeRange = overrides && 'createTimeRange' in overrides
      ? overrides.createTimeRange
      : filterCreateTimeRange;
    setSceneLoading(true);
    try {
      const idsArr = cleanIdTags(ids);
      const namesArr = cleanTags(names);
      const jirasArr = cleanTags(jiras);
      const labelsArr = cleanTags(labels);
      const sourcesArr = cleanTags(sources);
      const regionsArr = (cleanTags(regions) ?? []).map(r => r.toLowerCase()).filter(r => ['cn', 'ae', 'sea'].includes(r));
      const genModesArr = (cleanTags(genModes) ?? [])
        .filter((g): g is SceneGenMode => g === 'generate_and_qc_check' || g === 'cloud_cmd');
      const ctsIso = createTimeRange ? toIso(createTimeRange[0]) : undefined;
      const cteIso = createTimeRange ? toIso(createTimeRange[1]) : undefined;
      const resp = await getSceneInspectionList({
        current,
        pageSize,
        ...(idsArr ? { ids: idsArr } : {}),
        ...(namesArr ? { names: namesArr } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(jirasArr ? { jiraIssueCodes: jirasArr } : {}),
        ...(labelsArr ? { labels: labelsArr } : {}),
        ...(sourcesArr ? { sources: sourcesArr as SceneSource[] } : {}),
        ...(regionsArr.length ? { regions: regionsArr } : {}),
        ...(genModesArr.length ? { genModes: genModesArr } : {}),
        ...(treeNodeId !== undefined ? { treeNodeIds: [treeNodeId] } : {}),
        ...(ctsIso ? { createTimeStart: ctsIso } : {}),
        ...(cteIso ? { createTimeEnd: cteIso } : {}),
        includeDisabled: true,
      });
      setScenes(resp.list);
      setPagination({
        current: resp.page?.current ?? current,
        pageSize: resp.page?.pageSize ?? pageSize,
        total: resp.page?.totalElements ?? resp.total,
      });
    } finally {
      setSceneLoading(false);
    }
  }, [filterIds, filterNames, filterStatus, filterJiras, filterLabels, filterSources, filterRegions, filterGenModes, filterTreeNodeId, filterCreateTimeRange]);

  const loadLabelOptions = useCallback(async () => {
    setLabelLoading(true);
    try {
      const list = await getSceneLabels();
      setLabelOptions(list);
    } finally {
      setLabelLoading(false);
    }
  }, []);

  useEffect(() => { loadLabelOptions(); }, [loadLabelOptions]);

  // 场景状态下拉从 /scene/status 拉取(SceneStatusEnum),挂载一次即可;
  // 拉失败时静默保持空列表,不影响其他筛选。
  useEffect(() => {
    getSceneStatusList().then(setStatusOptions).catch(() => {});
  }, []);

  // URL 同步(仅 syncUrl=true)
  useEffect(() => {
    if (!syncUrl) return;
    const p = new URLSearchParams(searchParams);
    const setOrDel = (k: string, v: string | undefined) => {
      if (v) p.set(k, v);
      else p.delete(k);
    };
    setOrDel('ids', toCsv(filterIds));
    setOrDel('names', toCsv(filterNames));
    setOrDel('status', filterStatus !== undefined ? String(filterStatus) : undefined);
    setOrDel('jiraIssueCodes', toCsv(filterJiras));
    setOrDel('labels', toCsv(filterLabels));
    setOrDel('sources', toCsv(filterSources));
    setOrDel('regions', toCsv(filterRegions));
    setOrDel('genModes', toCsv(filterGenModes));
    setOrDel('treeNodeId', filterTreeNodeId !== undefined ? String(filterTreeNodeId) : undefined);
    setOrDel('createTimeStart', filterCreateTimeRange ? toIso(filterCreateTimeRange[0]) : undefined);
    setOrDel('createTimeEnd', filterCreateTimeRange ? toIso(filterCreateTimeRange[1]) : undefined);
    p.set('page', String(pagination.current));
    p.set('pageSize', String(pagination.pageSize));
    if (p.toString() !== searchParams.toString()) {
      setSearchParams(p, { replace: true });
    }
  }, [
    syncUrl,
    filterIds, filterNames, filterStatus, filterJiras, filterLabels, filterSources, filterRegions,
    filterGenModes, filterTreeNodeId, filterCreateTimeRange,
    pagination.current, pagination.pageSize,
    searchParams, setSearchParams,
  ]);

  // 首次拉一次
  useEffect(() => {
    fetchScenes(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // —— batch actions ——
  const handleBatchBuild = async () => {
    if (!selectedSceneIds.length) { message.warning('请先勾选场景'); return; }
    const ids = selectedSceneIds as number[];
    setBatchBuilding(true);
    try {
      const res = await batchGenerateSceneByIds(ids);
      // 链路连贯性:命中场景有构建在跑 → 后端拦下(不建 Job)返回 needConfirm。
      // 软提醒确认后带 force=true 重发,终止上一次构建、重新发起(latest-wins)。
      if (res?.needConfirm) {
        setBatchBuilding(false);
        Modal.confirm({
          title: '上一次构建还在进行中',
          content: res.message || `所选 ${ids.length} 个场景上一次构建还未完成,确认将终止上一次构建并重新发起?`,
          okText: '仍要重新发起',
          cancelText: '取消',
          onOk: async () => {
            await batchGenerateSceneByIds(ids, true);
            message.success(`已发起 ${ids.length} 个场景的构建任务`);
            setSelectedSceneIds([]);
            fetchScenes(pagination.current, pagination.pageSize);
          },
        });
        return;
      }
      message.success(`已发起 ${ids.length} 个场景的构建任务`);
      setSelectedSceneIds([]);
      fetchScenes(pagination.current, pagination.pageSize);
    } catch { /* interceptor handles */ } finally {
      setBatchBuilding(false);
    }
  };

  /** 批量加入到指定场景集(叶子节点):后端 batch-add 已存在,自动跳过已在集合内的。 */
  const handleAddToSetConfirm = async () => {
    if (!selectedSceneIds.length) {
      message.warning('请先勾选场景');
      return;
    }
    if (addToSetTarget === undefined) {
      message.warning('请选择目标场景集');
      return;
    }
    setAddToSetSubmitting(true);
    try {
      const res = await batchAddScenesToTree(
        addToSetTarget,
        selectedSceneIds as number[],
      );
      const { type, text } = formatBatchAddResult(res);
      message[type](text);
      setAddToSetOpen(false);
      setAddToSetTarget(undefined);
      // 不清 selectedSceneIds:用户可能想接着加到别的集合;列表本身不需要重拉
    } catch { /* interceptor handles */ } finally {
      setAddToSetSubmitting(false);
    }
  };

  const formatToggleResult = (
    res: { affected: number[]; alreadyInTargetState: number[]; notFound: number[] },
    verb: '禁用' | '恢复',
  ): string => {
    const parts: string[] = [];
    if (res.affected.length) parts.push(`${verb}成功 ${res.affected.length} 个`);
    if (res.alreadyInTargetState.length) {
      const tip = verb === '禁用' ? '已是禁用' : '未禁用';
      parts.push(`${res.alreadyInTargetState.length} 个${tip}`);
    }
    if (res.notFound.length) parts.push(`${res.notFound.length} 个场景不存在`);
    return parts.join('，') || '完成';
  };

  const handleBatchDisable = async () => {
    if (!selectedSceneIds.length) { message.warning('请先勾选场景'); return; }
    setBatchDisabling(true);
    try {
      const res = await disableScenes(selectedSceneIds as number[]);
      message.success(formatToggleResult(res, '禁用'));
      setSelectedSceneIds([]);
      fetchScenes(pagination.current, pagination.pageSize);
    } catch { /* interceptor handles */ } finally {
      setBatchDisabling(false);
    }
  };

  const handleSingleToggle = async (sceneId: number, action: 'disable' | 'restore') => {
    setTogglingSceneId(sceneId);
    try {
      const res = action === 'disable'
        ? await disableScenes([sceneId])
        : await restoreScenes([sceneId]);
      message.success(formatToggleResult(res, action === 'disable' ? '禁用' : '恢复'));
      fetchScenes(pagination.current, pagination.pageSize);
    } catch { /* interceptor handles */ } finally {
      setTogglingSceneId(null);
    }
  };

  const handleToggleSceneSecurity = async (sceneId: number, confidential: boolean) => {
    setActionPopoverSceneId(null);
    setTogglingSceneId(sceneId);
    try {
      await setScenesSecurityLevel([sceneId], confidential ? 0 : 1);
      message.success(confidential ? '已取消保密' : '已设为保密');
      fetchScenes(pagination.current, pagination.pageSize);
    } catch { /* interceptor handles */ } finally {
      setTogglingSceneId(null);
    }
  };

  // 场景名点击跳「场景标注」主编辑页 (SceneAnnotate,单场景视图),用 <Link> 渲染,
  // 使「右键/Ctrl+点击/中键」可原生在新标签页打开。左键跳转前先把当前筛选提交进 URL
  // 历史,便于从 SceneAnnotate navigate(-1) 返回时恢复筛选(仅 syncUrl 单页场景需要;
  // 新标签页打开走原生 href,不触发此副作用,新标签本就是全新上下文,无需恢复)。
  const commitFilterToHistory = useCallback(() => {
    // syncUrl=true 时(SceneGeneration 单页)当前 URL 已经带筛选,直接 flushSync 提交历史;
    // syncUrl=false 时(SceneManagement 嵌入)URL 由外层管理,不写。
    if (syncUrl) {
      flushSync(() => {
        setSearchParams(searchParams, { replace: true });
      });
    }
  }, [syncUrl, searchParams, setSearchParams]);

  // —— 列定义 + 列宽拖拽 ——
  const { columnWidths, resizeHeaderCell } = useResizableColumns({
    id: 80,
    name: 220,
    apiCode: 140,
    source: 100,
    genMode: 96,
    label: 120,
    mapName: 120,
    mapVersion: 100,
    status: 110,
    creator: 100,
    createTime: 180,
    sceneInfo: 200,
    sceneComment: 140,
    action: 72,
  });

  const columns: ColumnsType<SceneInspectionItem> = [
    { title: 'ID', dataIndex: 'id', width: columnWidths.id, onHeaderCell: resizeHeaderCell('id') },
    {
      title: '场景名称',
      dataIndex: 'name',
      width: columnWidths.name,
      onHeaderCell: resizeHeaderCell('name'),
      ellipsis: true,
      render: (name: string, record: SceneInspectionItem) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Link
            to={`/scene-annotate?sceneId=${record.id}`}
            onClick={commitFilterToHistory}
            style={{ color: '#5e6ad2', cursor: 'pointer' }}
          >
            {name}
          </Link>
          {(record.securityLevel ?? 0) > 0 && (
            <Tooltip title="保密：仅具备查看权限的角色可见">
              <LockFilled style={{ color: '#cf1322', fontSize: 12 }} />
            </Tooltip>
          )}
          {record.isDeleted ? (
            <Tooltip title="可见但运行任务被过滤">
              <Tag className="scene-disabled-tag" style={{ cursor: 'help' }}>已禁用</Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      width: columnWidths.apiCode,
      onHeaderCell: resizeHeaderCell('apiCode'),
      render: (v: string | null | undefined, record: SceneInspectionItem) => (
        <ApiCodeLink
          code={v}
          source={record.source as string | null | undefined}
          region={record.region as string | null | undefined}
        />
      ),
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: columnWidths.source,
      onHeaderCell: resizeHeaderCell('source'),
      render: (_: unknown, record: SceneInspectionItem) => <SceneSourceTag source={record.source} />,
    },
    {
      // 同一 apiCode 的事故 / 云控是两条独立场景，靠本列区分
      title: '场景类型',
      dataIndex: 'genMode',
      width: columnWidths.genMode,
      onHeaderCell: resizeHeaderCell('genMode'),
      render: (_: unknown, record: SceneInspectionItem) => (record.genMode === 'cloud_cmd'
        ? <Tag color="purple">云控</Tag>
        : <Tag>事故</Tag>),
    },
    {
      title: '标签',
      dataIndex: 'label',
      width: columnWidths.label,
      onHeaderCell: resizeHeaderCell('label'),
      ellipsis: true,
      render: (_: unknown, record: SceneInspectionItem) => formatLabelDisplay(record.label),
    },
    { title: '地图', dataIndex: 'mapName', width: columnWidths.mapName, onHeaderCell: resizeHeaderCell('mapName') },
    { title: '版本', dataIndex: 'mapVersion', width: columnWidths.mapVersion, onHeaderCell: resizeHeaderCell('mapVersion') },
    {
      title: '状态',
      dataIndex: 'statusName',
      width: columnWidths.status,
      onHeaderCell: resizeHeaderCell('status'),
      render: (_: string, record: SceneInspectionItem) => (
        <SceneStatusTag status={record.status} statusName={record.statusName} />
      ),
    },
    { title: '创建人', dataIndex: 'creator', width: columnWidths.creator, onHeaderCell: resizeHeaderCell('creator') },
    { title: '创建时间', dataIndex: 'createTime', width: columnWidths.createTime, onHeaderCell: resizeHeaderCell('createTime'), render: formatTime },
    {
      title: '备注',
      dataIndex: 'sceneInfo',
      width: columnWidths.sceneInfo,
      onHeaderCell: resizeHeaderCell('sceneInfo'),
      ellipsis: { showTitle: false },
      render: (v: string | null | undefined) => {
        const text = (v ?? '').trim();
        if (!text) return '-';
        return (
          <Tooltip placement="topLeft" title={text}>
            {text}
          </Tooltip>
        );
      },
    },
    ...(showCommentColumn ? [{
      title: '评论',
      key: 'sceneComment',
      width: columnWidths.sceneComment,
      onHeaderCell: resizeHeaderCell('sceneComment'),
      align: 'center' as const,
      fixed: 'right' as const,
      onCell: () => ({ style: { overflow: 'visible' } }),
      render: (_: unknown, record: SceneInspectionItem) => {
        const s = commentSummary[record.id];
        const hasComment = s != null && s.total > 0;
        const label = hasComment ? `${s.latestTitle ?? '评论'} (${s.total})` : '评论';
        return (
          <Button
            size="small"
            type="link"
            icon={<CommentOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setActionPopoverSceneId(null);
              setCommentTarget({ sceneId: record.id, sceneName: record.name ?? null });
            }}
            title={hasComment ? `最新：${s.latestTitle ?? ''}` : '点击查看评论'}
            style={{ padding: '0 4px', maxWidth: '100%', overflow: 'hidden' }}
          >
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{label}</span>
          </Button>
        );
      },
    }] : []),
    {
      title: '操作',
      key: 'action',
      width: columnWidths.action,
      onHeaderCell: resizeHeaderCell('action'),
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_: unknown, record: SceneInspectionItem) => (
        <Popover
          overlayClassName="job-action-popover"
          trigger="click"
          placement="bottomRight"
          open={actionPopoverSceneId === record.id}
          onOpenChange={(open) => setActionPopoverSceneId(open ? record.id : null)}
          content={
            <div className="job-action-menu" style={{ width: 180 }}>
              {record.isDeleted ? (
                <Popconfirm
                  title={`恢复场景「${record.name}」？`}
                  description="恢复后场景重新参与发任务相关查询"
                  okText="确认恢复"
                  cancelText="取消"
                  onConfirm={() => handleSingleToggle(record.id, 'restore')}
                >
                  <div className="job-action-menu__item">
                    <RollbackOutlined className="job-action-menu__icon" />
                    <span className="job-action-menu__label">恢复场景</span>
                  </div>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title={`禁用场景「${record.name}」？`}
                  description="禁用后所有发任务路径将自动过滤该场景;可在本列表点恢复撤销"
                  okText="确认禁用"
                  okButtonProps={{ style: { background: '#b45309', borderColor: '#b45309' } }}
                  cancelText="取消"
                  onConfirm={() => handleSingleToggle(record.id, 'disable')}
                >
                  <div className="job-action-menu__item job-action-menu__item--warning">
                    <StopOutlined className="job-action-menu__icon" />
                    <span className="job-action-menu__label">禁用场景</span>
                  </div>
                </Popconfirm>
              )}
              {canManageSecurity && (
                (record.securityLevel ?? 0) > 0 ? (
                  <div
                    className="job-action-menu__item"
                    onClick={() => handleToggleSceneSecurity(record.id, true)}
                  >
                    <UnlockOutlined className="job-action-menu__icon" />
                    <span className="job-action-menu__label">取消保密</span>
                  </div>
                ) : (
                  <div
                    className="job-action-menu__item job-action-menu__item--danger"
                    onClick={() => handleToggleSceneSecurity(record.id, false)}
                  >
                    <LockFilled className="job-action-menu__icon" />
                    <span className="job-action-menu__label">设为保密</span>
                  </div>
                )
              )}
            </div>
          }
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            loading={togglingSceneId === record.id}
          />
        </Popover>
      ),
    },
  ];

  const handleSearch = () => fetchScenes(1, pagination.pageSize);

  const enterToSearch = useEnterToSearch(handleSearch);

  const handleReset = () => {
    const ps = pagination.pageSize;
    setFilterIds([]);
    setFilterNames([]);
    setFilterStatus(undefined);
    setFilterJiras([]);
    setFilterLabels([]);
    setFilterSources([]);
    setFilterRegions([]);
    setFilterGenModes([]);
    setFilterTreeNodeId(undefined);
    setFilterCreateTimeRange(null);
    setPagination(prev => ({ ...prev, current: 1, total: 0 }));
    fetchScenes(1, ps, { ...emptyFilters });
  };

  return (
    // 组件自身 flex-column 撑满父容器:筛选栏 flex:0 固定顶部,表格 flex:1
    // 内滚。这样长列表滚动时筛选栏不会被推走,查询/重置按钮始终可见。
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
    }}>
      {/* 筛选栏(第一行) + 批量操作(第二行) —— 固定不滚 */}
      <div style={{
        marginBottom: 16,
        display: 'flex', flexDirection: 'column', gap: 12,
        flex: '0 0 auto',
      }}>
        <Space wrap>
          {/* 三个 tags 框:输入框有内容时 Enter 转标签,清空后 Enter 直接查询。
              不设 maxTagCount——Jira Key 这类值本身就长,一个标签几乎占满整框,
              折叠成「+N」等于看不见自己输了什么;放开后框会换行变高但内容始终可见。 */}
          <Select
            mode="tags"
            placeholder="场景 ID（多个回车/逗号分隔）"
            tokenSeparators={TOKEN_SEPARATORS}
            value={filterIds}
            onChange={(vs: string[]) => setFilterIds(vs.filter(v => /^\d+$/.test(v.trim())))}
            {...enterToSearch(1)}
            style={{ minWidth: 200 }}
            allowClear
          />
          <Select
            mode="tags"
            placeholder="场景名称（多个回车/逗号分隔）"
            tokenSeparators={TOKEN_SEPARATORS}
            value={filterNames}
            onChange={setFilterNames}
            {...enterToSearch(2)}
            style={{ minWidth: 240 }}
            allowClear
          />
          <Select
            mode="tags"
            placeholder="Jira Key（多个回车/逗号分隔）"
            tokenSeparators={TOKEN_SEPARATORS}
            value={filterJiras}
            onChange={setFilterJiras}
            {...enterToSearch(3)}
            style={{ minWidth: 220 }}
            allowClear
          />
          {/* 状态 / 标签 / 来源 / 场景树:回车即查询(下拉展开时先让位给选中)。 */}
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 140 }}
            value={filterStatus}
            onChange={v => setFilterStatus(v ?? undefined)}
            {...enterToSearch(4)}
            options={statusOptions}
          />
          <Select
            mode="multiple"
            placeholder="标签（可多选）"
            allowClear
            showSearch
            style={{ minWidth: 200 }}
            value={filterLabels}
            loading={labelLoading}
            onChange={setFilterLabels}
            {...enterToSearch(5)}
            options={labelOptions.map(l => ({ value: l, label: l }))}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            placeholder="来源（可多选）"
            allowClear
            style={{ minWidth: 180 }}
            value={filterSources}
            onChange={setFilterSources}
            {...enterToSearch(6)}
            options={SCENE_SOURCE_FILTER_OPTIONS}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            placeholder="区域（可多选）"
            allowClear
            style={{ minWidth: 160 }}
            value={filterRegions}
            onChange={setFilterRegions}
            {...enterToSearch(7)}
            options={[
              { value: 'cn', label: '国内' },
              { value: 'ae', label: '阿联酋' },
              { value: 'sea', label: '新加坡' },
            ]}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            placeholder="场景类型"
            allowClear
            style={{ minWidth: 140 }}
            value={filterGenModes}
            onChange={setFilterGenModes}
            {...enterToSearch(9)}
            options={[
              { value: 'generate_and_qc_check', label: '事故场景' },
              { value: 'cloud_cmd', label: '云控场景' },
            ]}
            maxTagCount="responsive"
          />
          <TreeSelect
            placeholder="场景树（目录/场景集）"
            allowClear
            showSearch
            treeNodeFilterProp="title"
            treeDefaultExpandAll={false}
            popupMatchSelectWidth={false}
            style={{ minWidth: 220 }}
            value={filterTreeNodeId}
            onChange={(v) => setFilterTreeNodeId(v as number | undefined)}
            {...enterToSearch(8)}
            treeData={filterTreeData}
          />
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm:ss' }}
            format={TIME_PICKER_FORMAT}
            placeholder={['创建开始时间', '创建结束时间']}
            value={filterCreateTimeRange}
            onChange={(range) => {
              if (!range || !range[0] || !range[1]) {
                setFilterCreateTimeRange(null);
              } else {
                setFilterCreateTimeRange([range[0], range[1]]);
              }
            }}
            style={{ minWidth: 360 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {selectedSceneIds.length > 0
              ? `已选 ${selectedSceneIds.length} 项`
              : '勾选场景后可批量操作'}
          </Text>
          <div style={{ flex: 1 }} />
          <Space>
            <Popconfirm
              title={`禁用选中的 ${selectedSceneIds.length} 个场景？`}
              description="禁用后所有发任务路径将自动过滤这些场景;可在本列表恢复"
              okText="确认禁用"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              disabled={!selectedSceneIds.length}
              onConfirm={handleBatchDisable}
            >
              {/* 批量禁用:红色 danger,强破坏性动作,视觉一眼可辨 */}
              <Button
                danger
                type="primary"
                icon={<StopOutlined />}
                loading={batchDisabling}
                disabled={!selectedSceneIds.length}
              >
                批量禁用
              </Button>
            </Popconfirm>
            {/* 批量构建:琥珀色,重量级操作但非破坏(用原「批量禁用」的黄色调)
                加二次确认避免误操作 —— 一次能触发 N 个场景重跑,成本不低 */}
            <Popconfirm
              title={`构建选中的 ${selectedSceneIds.length} 个场景？`}
              description="将为每个场景发起一次构建任务,成本较高,确认无误后再执行"
              okText="确认构建"
              okButtonProps={{ style: { background: '#b45309', borderColor: '#b45309' } }}
              cancelText="取消"
              disabled={!selectedSceneIds.length}
              onConfirm={handleBatchBuild}
            >
              <Button
                icon={<PlayCircleOutlined />}
                loading={batchBuilding}
                disabled={!selectedSceneIds.length}
                style={selectedSceneIds.length ? {
                  background: '#b45309',
                  borderColor: '#b45309',
                  color: '#fff',
                } : undefined}
              >
                批量构建
              </Button>
            </Popconfirm>
            {/* 加入场景集:primary 蓝,常规非破坏动作(接原「批量构建」的主色位) */}
            <Button
              type="primary"
              icon={<FolderAddOutlined />}
              disabled={!selectedSceneIds.length}
              onClick={() => setAddToSetOpen(true)}
            >
              加入场景集
            </Button>
          </Space>
        </div>
      </div>

      {/* 表格主体:flex:1 撑满剩余空间,内部滚 */}
      <div ref={tableScrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table<SceneInspectionItem>
          rowKey="id"
          columns={columns}
          dataSource={scenes}
          loading={sceneLoading}
          size="small"
          components={{ header: { cell: ResizableTitle } }}
          tableLayout="fixed"
          scroll={{ x: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}
          // 表头固定:sticky.getContainer 指向外层 overflow: auto 容器,
          // 让表头在容器内滚动时钉在顶部;window scroll 不参与(整个 App 已用
          // flex 撑满视口,窗口本身几乎不滚)。
          sticky={{ getContainer: () => tableScrollRef.current ?? window }}
          rowClassName={(r) => (r.isDeleted ? 'scene-row-disabled' : '')}
          rowSelection={{
            selectedRowKeys: selectedSceneIds,
            onChange: setSelectedSceneIds,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '20', '50', '100', '1000'],
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, size) => fetchScenes(page, size),
          }}
        />
      </div>

      {showCommentColumn && (
        <SceneCommentDrawer
          open={commentTarget !== null}
          onClose={() => { setCommentTarget(null); refreshCommentSummary(); }}
          sceneId={commentTarget?.sceneId ?? 0}
          sceneName={commentTarget?.sceneName ?? null}
          title="场景评论"
        />
      )}

      {/* 「加入集合」Modal:目标只能是叶子场景集,非叶子(目录)禁选。
          点确认走 batchAddScenesToTree,后端会自动跳过已在集合内的场景。 */}
      <Modal
        open={addToSetOpen}
        title={`加入场景集 · ${selectedSceneIds.length} 个场景`}
        okText="加入"
        cancelText="取消"
        confirmLoading={addToSetSubmitting}
        onOk={handleAddToSetConfirm}
        onCancel={() => {
          setAddToSetOpen(false);
          setAddToSetTarget(undefined);
        }}
        destroyOnHidden
      >
        <div style={{ marginBottom: 8, color: '#6b6f76', fontSize: 13 }}>
          选择一个目标场景集(仅可选叶子节点;已在集合内的场景会自动跳过)
        </div>
        <TreeSelect
          treeData={leafOnlyTreeData}
          value={addToSetTarget}
          onChange={(v) => setAddToSetTarget(v as number | undefined)}
          placeholder="选择目标场景集"
          allowClear
          showSearch
          treeNodeFilterProp="name"
          treeDefaultExpandAll={false}
          popupMatchSelectWidth={false}
          style={{ width: '100%' }}
        />
      </Modal>
    </div>
  );
};

export default FullSceneList;
